import type {
  CreateConnectSessionResponse,
  SaveSocialConnectionBody,
} from "@nlc/shared-types";

import Nango, { AuthError } from "@nangohq/frontend";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { toast } from "@/components/toast/toast-queue";
import { socialPublishingKeys } from "@/hooks/queryKeys";
import apiClient from "@/lib/api/client";

// Nango's default 500×600 is too narrow for Meta's login and permission screens.
const AUTH_WINDOW = { height: 750, width: 800 };
// Nango connect sessions expire after 30 minutes; refresh well before that.
const SESSION_REFRESH_MS = 20 * 60 * 1000;

async function createConnectSession(): Promise<CreateConnectSessionResponse> {
  return apiClient.url("/social-publishing/connect-session").post().json();
}

async function saveSocialConnection(
  body: SaveSocialConnectionBody,
): Promise<void> {
  await apiClient.url("/social-publishing/connection").post(body).res();
}

export function useConnectMeta({
  enabled,
  onConnected,
}: {
  enabled: boolean;
  onConnected: () => void;
}): {
  connect: () => Promise<void>;
  isConnecting: boolean;
} {
  const queryClient = useQueryClient();
  const [isConnecting, setIsConnecting] = useState(false);
  // Safari blocks popups not opened synchronously from the click, so the session is fetched before it.
  const session = useQuery({
    enabled,
    queryFn: createConnectSession,
    queryKey: socialPublishingKeys.connectSession(),
    refetchInterval: SESSION_REFRESH_MS,
    staleTime: SESSION_REFRESH_MS,
  });

  async function connect() {
    if (!session.data) {
      toast.add(
        { title: "Could not start the Meta connection", type: "error" },
        { timeout: 5000 },
      );
      session.refetch();

      return;
    }

    setIsConnecting(true);
    try {
      const { connectionId } = await new Nango({
        connectSessionToken: session.data.token,
        ...AUTH_WINDOW,
      }).auth(session.data.integrationId, { detectClosedAuthWindow: true });

      await saveConnection(connectionId);
    } catch (error) {
      if (!(error instanceof AuthError && error.type === "window_closed")) {
        toast.add(
          { title: "Could not connect your Meta account", type: "error" },
          { timeout: 5000 },
        );
      }
    } finally {
      setIsConnecting(false);
      // A session is spent once used, so the next attempt needs a fresh one.
      queryClient.invalidateQueries({ queryKey: socialPublishingKeys.all });
    }
  }

  async function saveConnection(connectionId: string) {
    try {
      await saveSocialConnection({ connectionId });
    } catch {
      toast.add(
        { title: "Could not save the Meta connection", type: "error" },
        { timeout: 5000 },
      );

      return;
    }
    onConnected();
  }

  return { connect, isConnecting: isConnecting || session.isLoading };
}
